import { flowLogicGroups, splitFlowRuleDescription } from '@shared/services/flowDescriptionCore.js'
import type { LogicCondition } from '@dashboard/types/config'

/**
 * Display model for condition logic: a top-level operator joining groups of
 * rules. Groups store their own operator; the editors keep it the opposite of
 * the top level.
 */
export type LogicOperator = 'and' | 'or'

export interface RuleToken {
  text: string
  kind: 'text' | 'field' | 'value'
}

export interface ConditionRuleSummary {
  category: string | null
  parts: RuleToken[]
  synced: boolean
}

export interface ConditionGroupSummary {
  operator: LogicOperator
  rules: ConditionRuleSummary[]
}

export interface ConditionSummary {
  operator: LogicOperator
  groups: ConditionGroupSummary[]
}

export const oppositeOperator = (operator: LogicOperator): LogicOperator => (operator === 'and' ? 'or' : 'and')

export const operatorTextClass: Record<LogicOperator, string> = {
  and: 'text-logic-and',
  or: 'text-logic-or'
}

export const operatorChipClass: Record<LogicOperator, string> = {
  and: 'bg-logic-and-soft text-logic-and',
  or: 'bg-logic-or-soft text-logic-or'
}

const isLogicGroup = (node: LogicCondition | undefined): boolean => Array.isArray(node?.and) || Array.isArray(node?.or)

/** True for a configured rule (or a negated/nested group holding one), not an empty placeholder. */
export const isConfiguredRule = (node: LogicCondition | undefined): boolean => {
  if (!node || typeof node !== 'object') {
    return false
  }
  if (node.not) {
    return isConfiguredRule(node.not)
  }
  if (isLogicGroup(node)) {
    return (node.and ?? node.or ?? []).some(isConfiguredRule)
  }
  return Boolean(node.$source || node.$type)
}

/** Top-level operator of a stored definition, matching how the runtime evaluates it. */
export const topLevelOperator = (definition: LogicCondition | undefined): LogicOperator =>
  flowLogicGroups(definition)?.operator ?? 'or'

const unavailable: RuleToken[] = [{ text: 'Rule description unavailable', kind: 'text' }]

/**
 * Builds the sentence model for a definition. `describe` returns the existing
 * `**bold**` description for one rule; rules are described independently so
 * each keeps its own category, field and value tokens.
 */
export async function summarizeCondition(
  definition: LogicCondition | undefined,
  describe: (rule: LogicCondition) => Promise<string | null>,
  { isSynced = () => false }: { isSynced?: (rule: LogicCondition) => boolean } = {}
): Promise<ConditionSummary> {
  const normalized = flowLogicGroups(definition)
  const operator: LogicOperator = normalized?.operator ?? 'or'
  const groups = await Promise.all(
    (normalized?.groups ?? []).map(async (group: LogicCondition): Promise<ConditionGroupSummary> => {
      const items = isLogicGroup(group) ? (group.and ?? group.or ?? []) : [group]
      const rules = await Promise.all(
        items.filter(isConfiguredRule).map(async (rule): Promise<ConditionRuleSummary> => {
          const description = await describe(rule).catch(() => null)
          const { category, parts } = description
            ? splitFlowRuleDescription(rule, description)
            : { category: null, parts: unavailable }
          return { category, parts, synced: isSynced(rule) }
        })
      )
      return {
        operator: group.and ? 'and' : group.or ? 'or' : oppositeOperator(operator),
        rules
      }
    })
  )
  return { operator, groups: groups.filter((group) => group.rules.length > 0) }
}

/**
 * Plain-text summary worded like `ConditionSentence`: rules are listed with the
 * group operator before the last one, and groups are joined by the top-level operator.
 */
export function conditionSummaryText(summary: ConditionSummary): string {
  return summary.groups
    .map(({ operator, rules }) =>
      rules
        .map((rule, index) => {
          const words = [rule.category, ...rule.parts.map((part) => part.text)].filter(Boolean).join(' ')
          const lead = index > 0 && index === rules.length - 1 ? `${operator} ` : ''
          const comma = rules.length > 2 && index < rules.length - 1 ? ',' : ''
          return `${lead}${words}${comma}`
        })
        .join(' ')
    )
    .join(` ${summary.operator} `)
    .replace(/\s+/g, ' ')
    .trim()
}
